import { type VoiceAssistantView, type VoiceCallView, VOICE_OUTCOME_LABELS } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { errorMessage, get, post, put } from "../../api";
import { Alert, Badge, Button, Checkbox, Field, Loading, Panel, PanelHeader, TextAreaField, formatDateTime } from "../../components/ui";
import { useSession } from "../../session";

/**
 * The AI phone assistant (Amazon Connect): on/off, where "talk to a person" goes, who hears about
 * messages, extra things it should know, the key its call flow uses, and the calls it answered.
 * It answers from the clinic's public information (the same as the website feed).
 */
export function PhoneAssistantTab() {
  const qc = useQueryClient();
  const { can } = useSession();
  const q = useQuery({ queryKey: ["voice-assistant"], queryFn: () => get<VoiceAssistantView>("/api/voice-assistant") });
  const [draft, setDraft] = useState<{ enabled: boolean; transferNumber: string; messageStaffIds: string[]; notes: string } | null>(null);
  const [key, setKey] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (q.data && !draft) setDraft({ enabled: q.data.enabled, transferNumber: q.data.transferNumber ?? "", messageStaffIds: q.data.messageStaffIds, notes: q.data.notes });
  }, [q.data, draft]);
  const save = useMutation({
    mutationFn: () => put<VoiceAssistantView>("/api/voice-assistant", draft),
    onSuccess: (v) => (qc.setQueryData(["voice-assistant"], v), setSaved(true)),
  });
  const newKey = useMutation({
    mutationFn: () => post<{ key: string; view: VoiceAssistantView }>("/api/voice-assistant/key", {}),
    onSuccess: (r) => (setKey(r.key), qc.setQueryData(["voice-assistant"], r.view)),
  });
  if (q.isLoading || !draft) return q.error ? <Alert>{errorMessage(q.error)}</Alert> : <Loading />;
  const v = q.data!;
  const set = (p: Partial<typeof draft>) => (setDraft({ ...draft, ...p }), setSaved(false));
  return (
    <div className="flex flex-col gap-5">
      <Panel>
        <PanelHeader
          title="AI phone assistant"
          description="Answers calls on its own number (forward your main line to it: all calls, after hours, or when nobody picks up). It answers from your public information: hours, address, the menu and prices, memberships, upcoming events, your cancellation policy, and the notes below. It greets clients by first name, texts callers the booking, event, membership, or forms link, takes messages, and transfers to a person when asked or unsure. It never gives medical advice or talks about anyone's visits, cards, or health."
        />
        <form
          className="flex flex-col gap-4 px-5 py-5"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Checkbox label="Answer calls" description="Off: calls to its number go straight to the transfer number." checked={draft.enabled} onChange={(enabled) => set({ enabled })} />
          <Field
            label="Transfer to"
            placeholder="(405) 555-0100"
            value={draft.transferNumber}
            onChange={(e) => set({ transferNumber: e.target.value })}
            hint="Where “talk to a person” goes: the front desk line or a ring group. Not the number that forwards to the assistant."
          />
          <div>
            <div className="mb-1 text-sm font-semibold">Who hears about messages</div>
            <div className="grid gap-1 sm:grid-cols-2">
              {v.staff.map((s) => (
                <Checkbox
                  key={s.id}
                  label={s.name}
                  description={s.hasMobile ? undefined : "No mobile on their staff page: emailed instead"}
                  checked={draft.messageStaffIds.includes(s.id)}
                  onChange={(on) => set({ messageStaffIds: on ? [...draft.messageStaffIds, s.id] : draft.messageStaffIds.filter((x) => x !== s.id) })}
                />
              ))}
            </div>
          </div>
          <TextAreaField
            label="Other things it should know"
            rows={5}
            value={draft.notes}
            onChange={(e) => set({ notes: e.target.value })}
            hint="In your words: parking, what to wear or bring, how long a drip takes, this month's special, what to say about walk-ins. Menu, prices, hours, events, and memberships come from Vaulted on their own."
          />
          {save.error != null && <Alert>{errorMessage(save.error)}</Alert>}
          {saved && <Alert tone="ok">Saved.</Alert>}
          <Button type="submit" variant="primary" className="self-start" busy={save.isPending}>
            Save
          </Button>
        </form>
      </Panel>

      <Panel>
        <PanelHeader title="Call flow key" description="The Amazon Connect call flow sends this with each request so Vaulted knows it's yours. Making a new one stops the old one." />
        <div className="flex flex-col gap-3 px-5 py-5 text-sm">
          <div className="text-muted">
            Tools address: <code className="break-all text-ink">{v.toolsUrl}</code>
          </div>
          {key && (
            <Alert tone="ok">
              Put this in the call flow's <code>vaultedKey</code> attribute. It's shown only now.
              <code className="mt-1 block break-all rounded bg-white px-2 py-1 text-xs text-ink">{key}</code>
            </Alert>
          )}
          {newKey.error != null && <Alert>{errorMessage(newKey.error)}</Alert>}
          <Button
            variant="secondary"
            className="self-start"
            busy={newKey.isPending}
            onClick={() => (!v.hasKey || window.confirm("Make a new key? The assistant stops working until the call flow has the new one.")) && newKey.mutate()}
          >
            {v.hasKey ? "Make a new key" : "Make a key"}
          </Button>
        </div>
      </Panel>

      {can("calls.view") && <AssistantCalls />}
    </div>
  );
}

/** The calls the assistant answered, newest first; messages it took can be marked done. */
export function AssistantCalls({ clientId }: { clientId?: string }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const q = useQuery({ queryKey: ["voice-calls", clientId ?? null], queryFn: () => get<VoiceCallView[]>(`/api/voice-calls${clientId ? `?clientId=${clientId}` : ""}`), enabled: can("calls.view") });
  const handled = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => post(`/api/voice-calls/${id}/handled`, { handled: on }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["voice-calls"] }),
  });
  const calls = q.data ?? [];
  if (!can("calls.view") || (clientId && !calls.length)) return null;
  return (
    <Panel>
      <PanelHeader title={clientId ? "Calls the phone assistant answered" : "Calls it answered"} />
      <div className="flex flex-col divide-y divide-line text-sm">
        {q.isLoading && <Loading />}
        {!q.isLoading && !calls.length && <p className="px-5 py-4 text-muted">No calls yet.</p>}
        {calls.map((c) => (
          <div key={c.id} className="flex flex-col gap-1 px-5 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{c.client ? <Link to={`/clients/${c.client.id}`} className="hover:underline">{c.client.name}</Link> : c.from}</span>
              <span className="text-muted">{formatDateTime(c.startedAt)}</span>
              <Badge tone={c.outcome === "message" && !c.handledAt ? "wait" : "off"}>{VOICE_OUTCOME_LABELS[c.outcome]}</Badge>
              {c.message?.urgent && <Badge tone="brand">Urgent</Badge>}
            </div>
            {c.summary && <p>{c.summary}</p>}
            {c.linksSent.length > 0 && <p className="text-muted">Texted: {c.linksSent.join(", ")}</p>}
            {c.message && (
              <div className="rounded-[var(--radius-control)] bg-mist px-3 py-2">
                <div>
                  <span className="font-semibold">{c.message.name || "Caller"}</span>
                  {c.message.callbackNumber ? ` · call back ${c.message.callbackNumber}` : ""}: {c.message.text}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted">
                  {c.handledAt ? `Done${c.handledBy ? ` by ${c.handledBy}` : ""} ${formatDateTime(c.handledAt)}` : "Not followed up yet"}
                  <Button variant="quiet" busy={handled.isPending && handled.variables?.id === c.id} onClick={() => handled.mutate({ id: c.id, on: !c.handledAt })}>
                    {c.handledAt ? "Undo" : "Mark done"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}
